/**
 * Tela /administracao/importacoes — consome o subsistema `core/importacoes/`.
 *
 * Fluxo (ADR-0007):
 *   1. Usuario escolhe o tipo de importacao (dropdown).
 *   2. Faz upload do arquivo .xlsm/.xlsx.
 *   3. Backend roda em DRY-RUN -> preview com contadores (criados/atualizados/
 *      ignorados/descartados/erros).
 *   4. Usuario confirma -> APPLY persiste atomico + registra historico +
 *      emite evento de auditoria.
 *
 * Permissoes:
 *   - `importacao:executar` (requer MFA recente) — botoes executar/confirmar
 *   - `importacao:view_historico` — listagem + historico
 *
 * 3 zonas (constitucional):
 *   Zona 1: EntityHeader (titulo)
 *   Zona 2: KpiStrip (total de importacoes ja feitas, ultimo apply etc)
 *   Zona 3: Card upload + Card historico
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiCheckCircle, FiDownload, FiRefreshCw, FiUploadCloud } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import { useAuth } from "../../../context/AuthContext";
import {
  baixarModelo,
  executarImportacao,
  listarHistorico,
  listarTipos,
  tipoToSlug,
} from "../../../services/importacoes";
import { obterPipeline } from "../../../services/crm";
import { TIPO_ETAPA_CONVERSAO } from "../../../utils/crmPermissoes";
import { PageContainer } from "../../../styles/shared";

import {
  Card,
  ErrorBox,
  FileDrop,
  HelpText,
  Pill,
  PrimaryButton,
  ResultCard,
  ResultGrid,
  ResultLabel,
  ResultValue,
  Row,
  SecondaryButton,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
} from "./styles";

function fmtData(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return "—";
  }
}

function fmtDuracao(ms) {
  if (!ms) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function ResultadoView({ resultado }) {
  if (!resultado) return null;
  return (
    <>
      <SectionTitle>
        {resultado.modo === "dry_run" ? "Preview (dry-run)" : "Resultado do apply"}
        <span style={{ marginLeft: 8, opacity: 0.65, fontWeight: 400 }}>
          {resultado.arquivo_nome} · {fmtDuracao(resultado.duracao_ms)}
        </span>
      </SectionTitle>
      <ResultGrid>
        <ResultCard $tone="default">
          <ResultLabel>Linhas processadas</ResultLabel>
          <ResultValue>{resultado.linhas_totais}</ResultValue>
        </ResultCard>
        <ResultCard $tone="success">
          <ResultLabel>{resultado.modo === "dry_run" ? "Seriam criados" : "Criados"}</ResultLabel>
          <ResultValue>{resultado.criados}</ResultValue>
        </ResultCard>
        <ResultCard $tone="info">
          <ResultLabel>{resultado.modo === "dry_run" ? "Seriam atualizados" : "Atualizados"}</ResultLabel>
          <ResultValue>{resultado.atualizados}</ResultValue>
        </ResultCard>
        <ResultCard $tone="neutral">
          <ResultLabel>Ignorados</ResultLabel>
          <ResultValue>{resultado.ignorados}</ResultValue>
        </ResultCard>
        <ResultCard $tone="warning">
          <ResultLabel>Descartados</ResultLabel>
          <ResultValue>{resultado.descartados}</ResultValue>
        </ResultCard>
        {resultado.erros > 0 && (
          <ResultCard $tone="danger">
            <ResultLabel>Erros</ResultLabel>
            <ResultValue>{resultado.erros}</ResultValue>
          </ResultCard>
        )}
      </ResultGrid>
    </>
  );
}

function Importacoes() {
  const { request, hasAllPermissions, isMaster } = useAuth();

  const podeExecutar = isMaster || hasAllPermissions(["importacao:executar"]);

  const [tipos, setTipos] = useState([]);
  const [tipoSel, setTipoSel] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [preview, setPreview] = useState(null); // dry-run result
  const [aplicado, setAplicado] = useState(null); // apply result
  const [executando, setExecutando] = useState(false);
  const [erro, setErro] = useState(null);

  // R20: alguns tipos exigem opcoes escolhidas aqui (ex.: leads -> etapa do CRM).
  const [etapas, setEtapas] = useState([]);
  const [etapaId, setEtapaId] = useState("");

  const [historico, setHistorico] = useState({ loading: true, items: [], total: 0, error: null });

  // Carrega tipos disponiveis.
  useEffect(() => {
    (async () => {
      try {
        const payload = await listarTipos(request);
        const items = payload?.tipos || [];
        setTipos(items);
        if (items.length > 0) setTipoSel(items[0].tipo);
      } catch (err) {
        setErro(err?.message || "Erro ao carregar tipos de importação.");
      }
    })();
  }, [request]);

  const carregarHistorico = useCallback(async () => {
    setHistorico((p) => ({ ...p, loading: true, error: null }));
    try {
      const payload = await listarHistorico(request, { pageSize: 20 });
      const items = payload?.results || [];
      setHistorico({
        loading: false,
        items,
        total: typeof payload?.count === "number" ? payload.count : items.length,
        error: null,
      });
    } catch (err) {
      setHistorico({ loading: false, items: [], total: 0, error: err?.message || "Erro ao carregar histórico." });
    }
  }, [request]);

  useEffect(() => {
    carregarHistorico();
  }, [carregarHistorico]);

  const tipoSelInfo = useMemo(
    () => tipos.find((t) => t.tipo === tipoSel) || null,
    [tipos, tipoSel]
  );

  const requerEtapa = Boolean(tipoSelInfo?.opcoes_aceitas?.includes("etapa_id"));

  // Carrega as etapas do pipeline quando o tipo exige etapa (leads). O usuario
  // escolhe a etapa do CRM aqui, durante a importacao (nao na planilha).
  useEffect(() => {
    if (!requerEtapa) return undefined;
    let vivo = true;
    (async () => {
      try {
        const pipeline = await obterPipeline(request);
        // CRM-02: a coluna de conversao nao entra no select. O importador
        // grava a etapa sem passar por `mover_lead`, entao o lead importado
        // ficaria parado em "Convertido" sem ganho e sem Cliente — o backend
        // tambem descarta esse destino e usa a etapa de entrada.
        const lista = (pipeline?.etapas || []).filter(
          (e) => e.tipo !== TIPO_ETAPA_CONVERSAO
        );
        if (!vivo) return;
        setEtapas(lista);
        const entrada = lista.find((e) => e.is_default_entrada) || lista[0];
        setEtapaId((atual) => atual || (entrada ? entrada.id : ""));
      } catch {
        /* silencioso — sem etapa, o backend usa a entrada padrao */
      }
    })();
    return () => {
      vivo = false;
    };
  }, [requerEtapa, request]);

  const handleArquivo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setArquivo(file);
    setPreview(null);
    setAplicado(null);
    setErro(null);
  };

  const handleDryRun = async () => {
    if (!arquivo || !tipoSel) return;
    setErro(null);
    setExecutando(true);
    setPreview(null);
    setAplicado(null);
    try {
      const resultado = await executarImportacao(request, tipoToSlug(tipoSel), {
        arquivo,
        dryRun: true,
        opcoes: requerEtapa ? { etapa_id: etapaId } : {},
      });
      setPreview(resultado);
    } catch (err) {
      setErro(err?.message || "Falha ao processar preview.");
    } finally {
      setExecutando(false);
    }
  };

  const handleApply = async () => {
    if (!arquivo || !tipoSel) return;
    setErro(null);
    setExecutando(true);
    try {
      const resultado = await executarImportacao(request, tipoToSlug(tipoSel), {
        arquivo,
        dryRun: false,
        opcoes: requerEtapa ? { etapa_id: etapaId } : {},
      });
      setAplicado(resultado);
      setPreview(null);
      // recarrega historico para mostrar a importacao recem feita
      carregarHistorico();
    } catch (err) {
      setErro(err?.message || "Falha ao aplicar importação.");
    } finally {
      setExecutando(false);
    }
  };

  const handleNovo = () => {
    setArquivo(null);
    setPreview(null);
    setAplicado(null);
    setErro(null);
  };

  const handleBaixarModelo = async () => {
    if (!tipoSelInfo?.tem_template) return;
    setErro(null);
    try {
      await baixarModelo(tipoToSlug(tipoSel));
    } catch (err) {
      setErro(err?.message || "Falha ao baixar o modelo.");
    }
  };

  const kpis = useMemo(
    () => [
      { label: "Tipos disponíveis", value: tipos.length },
      { label: "Importações no histórico", value: historico.total },
      {
        label: "Última importação",
        value: historico.items[0] ? fmtData(historico.items[0].created_at).split(" ")[0] : "—",
      },
    ],
    [tipos.length, historico.items, historico.total]
  );

  return (
    <PageContainer>
      <EntityHeader
        title="Importações"
        subtitle="Upload de planilhas externas para o PharuERP · dry-run obrigatório antes de aplicar"
        badges={
          podeExecutar
            ? []
            : [
                {
                  label: "Sem permissão de executar",
                  tone: "warning",
                  key: "perm",
                },
              ]
        }
        actions={[
          {
            label: "Atualizar histórico",
            icon: <FiRefreshCw size={14} aria-hidden="true" />,
            onClick: carregarHistorico,
          },
        ]}
      />

      <KpiStrip items={kpis} />

      <Card>
        <SectionTitle>1. Tipo + arquivo</SectionTitle>
        <Row>
          <Select
            value={tipoSel}
            onChange={(e) => {
              setTipoSel(e.target.value);
              setPreview(null);
              setAplicado(null);
            }}
            aria-label="Tipo de importação"
            disabled={tipos.length === 0}
          >
            {tipos.length === 0 && <option value="">Nenhum tipo disponível</option>}
            {tipos.map((t) => (
              <option key={t.tipo} value={t.tipo}>
                {t.descricao || t.tipo}
              </option>
            ))}
          </Select>
          {tipoSelInfo?.tem_template && (
            <SecondaryButton
              type="button"
              onClick={handleBaixarModelo}
              title="Baixar a planilha modelo deste tipo (cabeçalho pronto para preencher)"
            >
              <FiDownload size={14} aria-hidden="true" /> Baixar modelo
            </SecondaryButton>
          )}
        </Row>
        {tipoSelInfo && (
          <HelpText>
            Aba esperada: <strong>{tipoSelInfo.aba_esperada || "—"}</strong>
            {" · "}
            {tipoSelInfo.descricao}
          </HelpText>
        )}

        {requerEtapa && (
          <Row>
            <label
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                fontSize: 13,
                color: "var(--ink-2)",
              }}
            >
              Etapa do CRM para os leads importados
              <Select
                value={etapaId}
                onChange={(e) => setEtapaId(e.target.value)}
                aria-label="Etapa de destino dos leads"
              >
                {etapas.length === 0 && <option value="">Carregando etapas…</option>}
                {etapas.map((et) => (
                  <option key={et.id} value={et.id}>
                    {et.nome}
                  </option>
                ))}
              </Select>
            </label>
          </Row>
        )}

        <FileDrop>
          <FiUploadCloud size={28} aria-hidden="true" />
          <strong>{arquivo ? arquivo.name : "Clique para escolher um arquivo"}</strong>
          <span>Formatos aceitos: .xlsx, .xlsm</span>
          <input
            type="file"
            accept=".xlsx,.xlsm"
            onChange={handleArquivo}
            disabled={executando}
          />
        </FileDrop>

        {arquivo && (
          <Row>
            <PrimaryButton
              type="button"
              onClick={handleDryRun}
              disabled={executando || !podeExecutar}
              title={!podeExecutar ? "Sem permissão 'importação:executar'" : undefined}
            >
              {executando && !aplicado ? "Processando preview..." : "Pré-visualizar (dry-run)"}
            </PrimaryButton>
            <SecondaryButton type="button" onClick={handleNovo} disabled={executando}>
              Trocar arquivo
            </SecondaryButton>
          </Row>
        )}

        {erro && <ErrorBox>{erro}</ErrorBox>}
      </Card>

      {preview && (
        <Card>
          <ResultadoView resultado={preview} />
          <HelpText>
            <FiAlertTriangle size={14} aria-hidden="true" /> Preview confirmado.
            Nada foi salvo no banco ainda. Revise os contadores acima e clique em
            <strong> Confirmar e aplicar</strong> para persistir.
          </HelpText>
          <Row>
            <PrimaryButton type="button" onClick={handleApply} disabled={executando}>
              {executando ? "Aplicando..." : "Confirmar e aplicar"}
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => setPreview(null)} disabled={executando}>
              Cancelar
            </SecondaryButton>
          </Row>
        </Card>
      )}

      {aplicado && (
        <Card>
          <ResultadoView resultado={aplicado} />
          <HelpText>
            <FiCheckCircle size={14} aria-hidden="true" /> Importação concluída e
            registrada no histórico abaixo.
          </HelpText>
          <Row>
            <SecondaryButton type="button" onClick={handleNovo}>
              Nova importação
            </SecondaryButton>
          </Row>
        </Card>
      )}

      <Card>
        <SectionTitle>Histórico de importações</SectionTitle>
        {historico.error && <ErrorBox>{historico.error}</ErrorBox>}
        {historico.loading ? (
          <HelpText>Carregando...</HelpText>
        ) : historico.items.length === 0 ? (
          <HelpText>Nenhuma importação executada ainda.</HelpText>
        ) : (
          <TableWrapper>
            <Table>
              <colgroup>
                <col style={{ width: "18%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "8%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Tipo</th>
                  <th>Arquivo</th>
                  <th>Por</th>
                  <th>Resumo</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {historico.items.map((h) => {
                  const r = h.resultado || {};
                  const resumo = `${r.criados || 0}c / ${r.atualizados || 0}a / ${r.ignorados || 0}i / ${r.descartados || 0}d`;
                  return (
                    <tr key={h.id}>
                      <td title={fmtData(h.created_at)}>{fmtData(h.created_at)}</td>
                      <td title={h.tipo}>{h.tipo}</td>
                      <td title={h.arquivo_nome}>{h.arquivo_nome}</td>
                      <td title={h.executado_por?.email || ""}>
                        {h.executado_por?.nome || h.executado_por?.email || "—"}
                      </td>
                      <td title={resumo}>{resumo}</td>
                      <td>
                        <Pill $tone={h.sucesso ? "success" : "danger"}>
                          {h.sucesso ? "OK" : "Falha"}
                        </Pill>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>
    </PageContainer>
  );
}

export default Importacoes;
